/**
 * phi-product-zoom - Custom Element (Dawn-style, no Shadow DOM)
 * Hermès-inspired zoom modal with pan/drag and image gallery
 */

if (!customElements.get("phi-product-zoom")) {
  class PhiProductZoom extends HTMLElement {
    constructor() {
      super();

      this.MIN_ZOOM = 1;
      this.MAX_ZOOM = 4;
      this.ZOOM_STEP = 0.5;

      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.isDragging = false;
      this.isThumbDragging = false;
      this.isPinching = false;
      this.startX = 0;
      this.startY = 0;
      this.startTranslateX = 0;
      this.startTranslateY = 0;
      this.lastPinchDist = 0;
      this.images = [];
      this.currentIndex = 0;
      this.containerRect = null;
      this.imgBaseWidth = 0;
      this.imgBaseHeight = 0;
      this.rafPending = false;

      this.boundOnKeyDown = this.onKeyDown.bind(this);
      this.boundOnMouseDown = this.onMouseDown.bind(this);
      this.boundOnMouseMove = this.onMouseMove.bind(this);
      this.boundOnMouseUp = this.onMouseUp.bind(this);
      this.boundOnTouchStart = this.onTouchStart.bind(this);
      this.boundOnTouchMove = this.onTouchMove.bind(this);
      this.boundOnTouchEnd = this.onTouchEnd.bind(this);
      this.boundOnThumbPointerDown = this.onThumbPointerDown.bind(this);
      this.boundOnThumbPointerMove = this.onThumbPointerMove.bind(this);
      this.boundOnThumbPointerUp = this.onThumbPointerUp.bind(this);
      this.boundOnResize = this.onResize.bind(this);
      this.boundOnDocumentTouchMove = this.onDocumentTouchMove.bind(this);
      this.boundOnGesture = this.onGesture.bind(this);
    }

    connectedCallback() {
      this.render();
      this.cacheElements();
      this.attachEventListeners();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    render() {
      this.innerHTML = `
        <div class="phi-zoom-overlay" role="dialog" aria-modal="true" aria-label="Zoomed product image gallery">
          <div class="phi-zoom-container">
            <img class="phi-zoom-img" alt="Product image" draggable="false">
          </div>

          <button class="phi-zoom-close" aria-label="Close zoom">
            <svg viewBox="0 0 24 24" width="14" height="14">
              <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>

          <div class="phi-zoom-controls" role="group">
            <h3 class="visually-hidden" id="phi-zoom-label">Zoom</h3>
            <button class="phi-zoom-btn phi-zoom-in" aria-label="Zoom in">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M12 5v14M5 12h14"/>
              </svg>
              <span class="visually-hidden">Zoom in</span>
            </button>
            <div class="phi-zoom-track">
              <div class="phi-zoom-thumb" 
                   role="slider" 
                   tabindex="0" 
                   aria-orientation="vertical" 
                   aria-labelledby="phi-zoom-label"
                   aria-valuemin="100"
                   aria-valuemax="400">
              </div>
            </div>
            <button class="phi-zoom-btn phi-zoom-out" aria-label="Zoom out">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M5 12h14"/>
              </svg>
              <span class="visually-hidden">Zoom out</span>
            </button>
          </div>

          <div class="phi-zoom-gallery" role="tablist" aria-label="Image gallery"></div>
        </div>
      `;
    }

    cacheElements() {
      this.overlay = this.querySelector(".phi-zoom-overlay");
      this.container = this.querySelector(".phi-zoom-container");
      // this.bgImg = this.querySelector(".phi-zoom-bg");
      this.zoomImg = this.querySelector(".phi-zoom-img");
      this.closeBtn = this.querySelector(".phi-zoom-close");
      this.zoomInBtn = this.querySelector(".phi-zoom-in");
      this.zoomOutBtn = this.querySelector(".phi-zoom-out");
      this.track = this.querySelector(".phi-zoom-track");
      this.thumb = this.querySelector(".phi-zoom-thumb");
      this.gallery = this.querySelector(".phi-zoom-gallery");
    }

    attachEventListeners() {
      this.closeBtn.addEventListener("click", () => this.close());

      this.overlay.addEventListener("click", (e) => {
        if (e.target === this.overlay) this.close();
      });

      this.zoomInBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.zoomIn();
      });

      this.zoomOutBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.zoomOut();
      });

      document.addEventListener("keydown", this.boundOnKeyDown);

      // Override CSS pointer-events:none so the image receives drag events
      this.zoomImg.style.pointerEvents = "auto";
      this.zoomImg.style.cursor = "grab";

      // Mouse drag on the IMAGE
      this.zoomImg.addEventListener("mousedown", this.boundOnMouseDown);

      // Touch drag + pinch on the IMAGE
      this.zoomImg.addEventListener("touchstart", this.boundOnTouchStart, {
        passive: false,
      });
      this.zoomImg.addEventListener("touchmove", this.boundOnTouchMove, {
        passive: false,
      });
      this.zoomImg.addEventListener("touchend", this.boundOnTouchEnd);

      // Thumb drag
      this.thumb.addEventListener("mousedown", this.boundOnThumbPointerDown);
      this.thumb.addEventListener("touchstart", this.boundOnThumbPointerDown, {
        passive: true,
      });
      this.track.addEventListener("click", (e) => this.onTrackClick(e));

      this.thumb.addEventListener("keydown", (e) => {
        if (e.key === "ArrowUp") {
          e.preventDefault();
          this.zoomIn();
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          this.zoomOut();
        }
      });

      this.zoomImg.addEventListener("load", () => {
        this.scale = this.calculateFillZoom();
        this.translateX = 0;
        this.translateY = 0;
        this.cacheGeometry();
        this.clampTranslate();
        this.updateUI();
        this.zoomImg.style.touchAction = "none";
        this.zoomImg.classList.add("loaded");
      });

      window.addEventListener("resize", this.boundOnResize, { passive: true });

      this.gallery.addEventListener("click", (e) => {
        const btn = e.target.closest(".phi-zoom-gallery-item");
        if (btn) {
          const index = parseInt(btn.dataset.index, 10);
          this.goToImage(index);
        }
      });
    }

    removeEventListeners() {
      document.removeEventListener("keydown", this.boundOnKeyDown);
      document.removeEventListener("mousemove", this.boundOnMouseMove);
      document.removeEventListener("mouseup", this.boundOnMouseUp);
      document.removeEventListener("mousemove", this.boundOnThumbPointerMove);
      document.removeEventListener("mouseup", this.boundOnThumbPointerUp);
      document.removeEventListener("touchmove", this.boundOnThumbPointerMove);
      document.removeEventListener("touchend", this.boundOnThumbPointerUp);
      window.removeEventListener("resize", this.boundOnResize);
    }

    // === PUBLIC METHODS ===

    /**
     * @param {Array} images - Array of { src, thumb, alt } objects
     * @param {number} startIndex - Index of the image to start with
     */
    open(images, startIndex = 0) {
      this.images = images;
      this.currentIndex = startIndex;
      this.scale = this.MIN_ZOOM;
      this.translateX = 0;
      this.translateY = 0;

      this.renderGallery();
      this.loadImage(this.currentIndex);

      this.classList.add("open");
      this.overlay.classList.add("appear");
      document.body.style.overflow = "hidden";
      // iOS WebKit: prevent page scroll/gesture stealing frames during drag
      document.addEventListener("touchmove", this.boundOnDocumentTouchMove, {
        passive: false,
      });
      document.addEventListener("gesturestart", this.boundOnGesture, {
        passive: false,
      });
      document.addEventListener("gesturechange", this.boundOnGesture, {
        passive: false,
      });
      document.addEventListener("gestureend", this.boundOnGesture, {
        passive: false,
      });

      setTimeout(() => this.closeBtn.focus(), 100);

      this.dispatchEvent(
        new CustomEvent("zoom:open", {
          bubbles: true,
          detail: { index: startIndex },
        }),
      );
    }

    /**
     * @param {HTMLImageElement} img - Source image element
     * @param {string} highResUrl - Optional high-res URL
     */
    openSingle(img, highResUrl) {
      const url =
        highResUrl ||
        img.src.replace(/\d+x\d+/, "2000x2000").replace(/_\d+[a-z]\./, "_q99.");
      const thumbUrl = img.src;
      const alt = img.alt || "Product image";

      this.open([{ src: url, thumb: thumbUrl, alt }], 0);
    }

    close() {
      this.overlay.classList.add("disappear");
      this.overlay.classList.remove("appear");

      setTimeout(() => {
        this.classList.remove("open");
        this.overlay.classList.remove("disappear");
        document.body.style.overflow = "";
        document.removeEventListener("touchmove", this.boundOnDocumentTouchMove);
        document.removeEventListener("gesturestart", this.boundOnGesture);
        document.removeEventListener("gesturechange", this.boundOnGesture);
        document.removeEventListener("gestureend", this.boundOnGesture);

        this.dispatchEvent(new CustomEvent("zoom:close", { bubbles: true }));
      }, 200);
    }

    /**
     * @param {number} index - Image index
     */
    goToImage(index) {
      if (index < 0 || index >= this.images.length) return;

      this.currentIndex = index;
      this.scale = this.MIN_ZOOM;
      this.translateX = 0;
      this.translateY = 0;

      this.loadImage(index);
      this.updateGallerySelection();
      this.updateUI();
    }

    // === PRIVATE METHODS ===

    loadImage(index) {
      const image = this.images[index];
      if (!image) return;

      this.zoomImg.classList.remove("loaded");
      this.zoomImg.src = image.src;
      this.zoomImg.alt = image.alt || `Product image ${index + 1}`;
      // this.bgImg.style.backgroundImage = `url("${image.thumb}")`;
    }

    renderGallery() {
      if (this.images.length <= 1) {
        this.gallery.style.display = "none";
        return;
      }

      this.gallery.style.display = "";
      this.gallery.innerHTML = `
        <div class="phi-zoom-gallery-wrapper">
          ${this.images
            .map(
              (img, i) => `
            <button class="phi-zoom-gallery-item ${i === this.currentIndex ? "active" : ""}"
                    role="tab"
                    aria-selected="${i === this.currentIndex}"
                    aria-label="View image ${i + 1} of ${this.images.length}"
                    data-index="${i}"
                    tabindex="${i === this.currentIndex ? "0" : "-1"}">
              <img src="${img.thumb}" alt="" width="38" height="36" loading="lazy">
            </button>
          `,
            )
            .join("")}
          <div class="phi-zoom-gallery-indicator"></div>
        </div>
      `;

      this.updateGalleryIndicator();
    }

    updateGallerySelection() {
      const items = this.gallery.querySelectorAll(".phi-zoom-gallery-item");
      items.forEach((item, i) => {
        const isActive = i === this.currentIndex;
        item.classList.toggle("active", isActive);
        item.setAttribute("aria-selected", isActive);
        item.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      this.updateGalleryIndicator();
    }

    updateGalleryIndicator() {
      const indicator = this.gallery.querySelector(
        ".phi-zoom-gallery-indicator",
      );
      if (!indicator) return;

      const activeItem = this.gallery.querySelector(
        ".phi-zoom-gallery-item.active",
      );
      if (activeItem) {
        indicator.style.transform = `translate3d(${activeItem.offsetLeft}px, 0, 0)`;
        indicator.style.width = `${activeItem.getBoundingClientRect().width}px`;
      }
    }

    calculateFillZoom() {
      const containerW = this.container.clientWidth;
      const containerH = this.container.clientHeight;
      const imgW = this.zoomImg.offsetWidth;
      const imgH = this.zoomImg.offsetHeight;

      if (!imgW || !imgH) return this.MIN_ZOOM;

      // Scale needed for the image to fully cover the container (no empty space)
      const fillScale = Math.max(containerW / imgW, containerH / imgH);
      return Math.max(this.MIN_ZOOM, Math.min(this.MAX_ZOOM, fillScale));
    }

    applyTransform() {
      // translate3d improves compositing stability on iOS WebKit
      this.zoomImg.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
    }

    cacheGeometry() {
      this.containerRect = this.container.getBoundingClientRect();
      this.imgBaseWidth = this.zoomImg.offsetWidth || 0;
      this.imgBaseHeight = this.zoomImg.offsetHeight || 0;
    }

    clampTranslate() {
      const containerRect = this.containerRect || this.container.getBoundingClientRect();
      // offsetWidth/offsetHeight are NOT affected by CSS transforms, so multiply by current scale
      const baseW = this.imgBaseWidth || this.zoomImg.offsetWidth;
      const baseH = this.imgBaseHeight || this.zoomImg.offsetHeight;
      const scaledWidth = baseW * this.scale;
      const scaledHeight = baseH * this.scale;

      const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
      const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

      this.translateX = Math.max(-maxX, Math.min(maxX, this.translateX));
      this.translateY = Math.max(-maxY, Math.min(maxY, this.translateY));

      this.applyTransform();
    }

    scheduleClampTranslate() {
      if (this.rafPending) return;
      this.rafPending = true;
      requestAnimationFrame(() => {
        this.rafPending = false;
        this.clampTranslate();
      });
    }

    beginInteraction() {
      this.zoomImg.classList.add("is-interacting");
    }

    endInteraction() {
      this.zoomImg.classList.remove("is-interacting");
    }

    zoomIn() {
      this.scale = Math.min(this.MAX_ZOOM, this.scale + this.ZOOM_STEP);
      this.clampTranslate();
      this.updateUI();
    }

    zoomOut() {
      this.scale = Math.max(this.MIN_ZOOM, this.scale - this.ZOOM_STEP);
      if (this.scale <= this.MIN_ZOOM) {
        this.translateX = 0;
        this.translateY = 0;
      }
      this.clampTranslate();
      this.updateUI();
    }

    zoomTo(newScale, centerX, centerY) {
      newScale = Math.max(this.MIN_ZOOM, Math.min(this.MAX_ZOOM, newScale));
      if (newScale === this.scale) return;

      if (centerX !== undefined && centerY !== undefined) {
        const ratio = newScale / this.scale;
        this.translateX = centerX - (centerX - this.translateX) * ratio;
        this.translateY = centerY - (centerY - this.translateY) * ratio;
      }

      this.scale = newScale;
      if (this.scale <= this.MIN_ZOOM) {
        this.translateX = 0;
        this.translateY = 0;
      }
      this.clampTranslate();
      this.updateUI();
    }

    setScaleFromThumbPosition(percent) {
      const pct = Math.max(0, Math.min(100, percent));
      this.scale =
        this.MIN_ZOOM + (pct / 100) * (this.MAX_ZOOM - this.MIN_ZOOM);
      if (this.scale <= this.MIN_ZOOM) {
        this.translateX = 0;
        this.translateY = 0;
      }
      this.clampTranslate();
      this.updateUI();
    }

    updateUI() {
      this.zoomInBtn.disabled = this.scale >= this.MAX_ZOOM;
      this.zoomOutBtn.disabled = this.scale <= this.MIN_ZOOM;

      const pct =
        (this.scale - this.MIN_ZOOM) / (this.MAX_ZOOM - this.MIN_ZOOM);
      const trackHeight = this.track.offsetHeight || 240;
      const splitPx = pct * trackHeight;

      this.track.style.background = `linear-gradient(0deg, rgb(68, 68, 68) ${splitPx}px, rgb(145, 145, 145) ${splitPx}px)`;
      this.thumb.style.bottom = `${splitPx}px`;
      this.thumb.style.top = "auto";
      this.thumb.style.transform = "translate(-50%, 50%)";
      this.thumb.setAttribute("aria-valuenow", Math.round(this.scale * 100));
      this.thumb.setAttribute(
        "aria-valuetext",
        `scale at ${Math.round(this.scale * 100)}%`,
      );

      const zoomInLabel = this.zoomInBtn.querySelector(".visually-hidden");
      const zoomOutLabel = this.zoomOutBtn.querySelector(".visually-hidden");
      if (zoomInLabel)
        zoomInLabel.textContent = `Zoom in, current ${Math.round(this.scale * 100)}%`;
      if (zoomOutLabel)
        zoomOutLabel.textContent = `Zoom out, current ${Math.round(this.scale * 100)}%`;
    }

    // === EVENT HANDLERS ===

    onKeyDown(e) {
      if (!this.classList.contains("open")) return;

      if (e.key === "Escape") {
        this.close();
      } else if (e.key === "ArrowLeft") {
        this.goToImage(this.currentIndex - 1);
      } else if (e.key === "ArrowRight") {
        this.goToImage(this.currentIndex + 1);
      }
    }

    // --- Mouse drag (bound to the image) ---

    onMouseDown(e) {
      if (this.scale <= this.MIN_ZOOM) return;

      e.preventDefault();
      this.beginInteraction();
      this.isDragging = true;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.startTranslateX = this.translateX;
      this.startTranslateY = this.translateY;
      this.zoomImg.style.cursor = "grabbing";
      this.cacheGeometry();

      document.addEventListener("mousemove", this.boundOnMouseMove);
      document.addEventListener("mouseup", this.boundOnMouseUp);
    }

    onMouseMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();

      this.translateX = this.startTranslateX + (e.clientX - this.startX);
      this.translateY = this.startTranslateY + (e.clientY - this.startY);
      this.scheduleClampTranslate();
    }

    onMouseUp() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.endInteraction();
      this.zoomImg.style.cursor = "grab";
      this.clampTranslate();

      document.removeEventListener("mousemove", this.boundOnMouseMove);
      document.removeEventListener("mouseup", this.boundOnMouseUp);
    }

    // --- Touch: unified single-finger drag + two-finger pinch (bound to the image) ---

    onTouchStart(e) {
      if (e.touches.length === 2) {
        this.isDragging = false;
        this.isPinching = true;
        this.lastPinchDist = 0;
        e.preventDefault();
        this.beginInteraction();
        this.cacheGeometry();
      } else if (e.touches.length === 1 && this.scale > this.MIN_ZOOM) {
        this.isDragging = true;
        this.isPinching = false;
        this.beginInteraction();
        this.startX = e.touches[0].clientX;
        this.startY = e.touches[0].clientY;
        this.startTranslateX = this.translateX;
        this.startTranslateY = this.translateY;
        this.cacheGeometry();
      }
    }

    onTouchMove(e) {
      if (e.touches.length === 2 && this.isPinching) {
        e.preventDefault();
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY,
        );
        if (this.lastPinchDist > 0) {
          const delta = (dist - this.lastPinchDist) * 0.01;
          const containerRect =
            this.containerRect || this.container.getBoundingClientRect();
          // Center relative to transform-origin (container center)
          const centerX =
            (e.touches[0].clientX + e.touches[1].clientX) / 2 -
            containerRect.left -
            containerRect.width / 2;
          const centerY =
            (e.touches[0].clientY + e.touches[1].clientY) / 2 -
            containerRect.top -
            containerRect.height / 2;
          this.zoomTo(this.scale + delta, centerX, centerY);
        }
        this.lastPinchDist = dist;
      } else if (e.touches.length === 1 && this.isDragging) {
        e.preventDefault();
        this.translateX =
          this.startTranslateX + (e.touches[0].clientX - this.startX);
        this.translateY =
          this.startTranslateY + (e.touches[0].clientY - this.startY);
        this.scheduleClampTranslate();
      }
    }

    onTouchEnd(e) {
      if (e.touches.length < 2 && this.isPinching) {
        this.isPinching = false;
        this.lastPinchDist = 0;
        if (!this.isDragging) this.endInteraction();
      }
      if (e.touches.length === 0 && this.isDragging) {
        this.isDragging = false;
        this.endInteraction();
        this.clampTranslate();
      }
    }

    onResize() {
      if (!this.classList.contains("open")) return;
      this.cacheGeometry();
      this.clampTranslate();
    }

    onDocumentTouchMove(e) {
      // Only block scroll while the zoom is actively being manipulated
      if (!this.classList.contains("open")) return;
      if (this.isDragging || this.isPinching || this.isThumbDragging) {
        e.preventDefault();
      }
    }

    onGesture(e) {
      if (!this.classList.contains("open")) return;
      e.preventDefault();
    }

    // --- Thumb slider ---

    onThumbPointerDown(e) {
      e.stopPropagation();
      this.isThumbDragging = true;
      document.addEventListener("mousemove", this.boundOnThumbPointerMove);
      document.addEventListener("mouseup", this.boundOnThumbPointerUp);
      document.addEventListener("touchmove", this.boundOnThumbPointerMove, {
        passive: false,
      });
      document.addEventListener("touchend", this.boundOnThumbPointerUp);
    }

    onThumbPointerMove(e) {
      if (!this.isThumbDragging) return;
      e.preventDefault();

      const rect = this.track.getBoundingClientRect();
      const y = e.clientY ?? e.touches?.[0]?.clientY;
      const pct = ((rect.bottom - y) / rect.height) * 100;
      this.setScaleFromThumbPosition(pct);
    }

    onThumbPointerUp() {
      this.isThumbDragging = false;
      document.removeEventListener("mousemove", this.boundOnThumbPointerMove);
      document.removeEventListener("mouseup", this.boundOnThumbPointerUp);
      document.removeEventListener("touchmove", this.boundOnThumbPointerMove);
      document.removeEventListener("touchend", this.boundOnThumbPointerUp);
    }

    onTrackClick(e) {
      if (e.target === this.thumb) return;
      const rect = this.track.getBoundingClientRect();
      const pct = ((rect.bottom - e.clientY) / rect.height) * 100;
      this.setScaleFromThumbPosition(pct);
    }
  }

  customElements.define("phi-product-zoom", PhiProductZoom);
}

// === AUTO-INITIALIZE FOR PRODUCT IMAGES ===
(function () {
  let zoomElement = document.querySelector("phi-product-zoom");
  if (!zoomElement) {
    zoomElement = document.createElement("phi-product-zoom");
    document.body.appendChild(zoomElement);
  }

  function init() {
    document
      .querySelectorAll(
        ".phi-p--main-swiper .swiper-slide img.phi-zoom-trigger, .phi-p--main-swiper .swiper-slide img[data-zoom-src]",
      )
      .forEach((img) => {
        if (img.closest(".phi-p--spacer-slide")) return;
        if (img.dataset.zoomInit) return;
        img.dataset.zoomInit = "1";
        img.style.cursor = "pointer";

        img.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          const swiper = img.closest(".phi-p--main-swiper");
          const allImages = swiper
            ? Array.from(
                swiper.querySelectorAll(
                  ".swiper-slide img:not(.phi-p--spacer-slide img)",
                ),
              ).filter((i) => !i.closest(".phi-p--spacer-slide"))
            : [img];

          const images = allImages.map((i) => ({
            src:
              i.dataset.zoomSrc ||
              i.src
                .replace(/\d+x\d+/, "2000x2000")
                .replace(/_\d+[a-z]\./, "_q99."),
            thumb: i.src,
            alt: i.alt || "Product image",
          }));

          const startIndex = allImages.indexOf(img);
          zoomElement.open(images, startIndex >= 0 ? startIndex : 0);
        });
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
  document.addEventListener("product-info:loaded", () => setTimeout(init, 100));
})();
